import { lngLatToMercator }      from '@tokamap/core';
import type { LatestRawFeature } from '@tokamap/core';
import type { Shape }            from '@tokamap/core';
import type { TokaMapInstance }  from '@tokamap/core';
import type { DxfPlacement }     from '@models/map/dxfTypes';

/**
 * Placement INTERACTIF du fond d'un import de plan (DXF / SVG / PDF) sur la carte.
 *
 * Le serveur rend le plan en SVG avec une transform GELÉE (monde CAO -> pixels, boîte
 * `width × height` renvoyée par `/plan/preview`) et attend, pour générer les features, un
 * descripteur de placement en EPSG:3857 : `{center, scale (mètres/pixel), rotation (radians
 * trigonométriques)}` — voir `PlacementProjector` côté service `map`.
 *
 * Le fond est posé comme une forme-image (`shapeKind: "image"`, ratio verrouillé au
 * redimensionnement) que l'opérateur déplace / redimensionne / pivote comme n'importe quelle
 * autre forme ; {@link computePlacementFromShape} relit ensuite le placement courant depuis ses
 * 4 coins. Fond et features partagent la même transform gelée : l'alignement est acquis par
 * construction, sans calage point à point.
 *
 * La forme n'est JAMAIS persistée (`skipSave`) : c'est un échafaudage de placement, pas une
 * forme de la carte — d'où l'absence d'appel à `onSave` sur sa création, ses déplacements et sa
 * suppression.
 */

/** Part du viewport occupée par le fond quand le plan n'est pas géoréférencé (pré-placement). */
const FALLBACK_VIEWPORT_RATIO = 0.8;

/**
 * Options communes à toutes les mutations d'un échafaudage d'import : jamais sauvegardé, jamais
 * dans l'historique.
 *
 * Exporté parce que l'aperçu global des formes (`helpers/planShapes.ts`) pose lui aussi des
 * formes qui ne doivent devenir ni des Features ni des étapes d'undo — les redéclarer ailleurs,
 * ce serait s'offrir de les oublier.
 */
export const SCAFFOLD_OPTIONS = { skipSave: true, skipHistory: true } as const;

/**
 * Préfixe des ids de fond. Il rend la forme reconnaissable partout, en particulier dans le
 * `onSave` de l'éditeur (`TokamapEditor/index.tsx`), qui refuse de l'enregistrer : `skipSave` ne
 * couvre que les gestes déclenchés d'ici, pas ceux que l'opérateur peut lancer lui-même sur la
 * forme (double-clic, "Modifier" du menu contextuel, Suppr) — le préfixe, lui, les couvre tous.
 */
export const BACKGROUND_ID_PREFIX = 'plan-background-';

/** Id de la forme-image de fond, stable pour un import donné (pas de recherche par `imageSrc`). */
export function backgroundShapeId(importId: number): string {
	return `${BACKGROUND_ID_PREFIX}${importId}`;
}

/**
 * Id de la forme-image d'APERÇU d'un calque, à l'étape de mapping.
 *
 * Distinct du fond d'import ({@link backgroundShapeId}) — les deux ne coexistent jamais, mais un
 * id propre évite qu'un aperçu resté en place soit pris pour le fond, puis relu comme placement à
 * la génération. Il garde le préfixe commun : les gardes anti-enregistrement portent dessus.
 */
export function layerPreviewShapeId(importId: number): string {
	return `${BACKGROUND_ID_PREFIX}${importId}-layer`;
}

/**
 * Id de la forme-image de FOND de l'aperçu global des formes, à l'étape de mapping.
 *
 * Encore un id distinct : l'aperçu global et l'aperçu d'un calque ne coexistent pas, mais les
 * confondre ferait retirer l'un en croyant retirer l'autre — et le fond resté seul sur la carte
 * n'aurait plus rien pour le piloter. Même préfixe commun, donc mêmes gardes anti-enregistrement.
 */
export function planSourceShapeId(importId: number): string {
	return `${BACKGROUND_ID_PREFIX}${importId}-source`;
}

/** Vrai pour la forme-image de fond d'un import de plan — voir {@link BACKGROUND_ID_PREFIX}. */
export function isBackgroundShapeId(shapeId: string): boolean {
	return shapeId.startsWith(BACKGROUND_ID_PREFIX);
}

/**
 * Pré-placement quand le plan n'est PAS géoréférencé : le fond est centré sur la vue courante et
 * dimensionné pour occuper ~80 % du viewport (axe le plus contraint, pour que tout le plan reste
 * visible), sans rotation. L'opérateur ajuste ensuite.
 *
 * `null` si la vue n'est pas exploitable (carte pas encore prête, dimensions pixel absentes).
 */
export function computeFallbackPlacement(
	instance: TokaMapInstance,
	widthPx: number,
	heightPx: number,
): DxfPlacement | null {
	if (!widthPx || !heightPx) return null;

	const bounds = instance.mapAdapter.getViewportBounds();
	// `getViewportBounds` renvoie des degrés (ouest/sud/est/nord) : on repasse en 3857, seul repère
	// dans lequel le placement a un sens (mètres, comme `scale`).
	const [westX, southY] = lngLatToMercator([bounds.minX, bounds.minY]);
	const [eastX, northY] = lngLatToMercator([bounds.maxX, bounds.maxY]);

	const viewportWidth = Math.abs(eastX - westX);
	const viewportHeight = Math.abs(northY - southY);
	if (!viewportWidth || !viewportHeight) return null;

	// mètres/pixel du fond pour tenir dans FALLBACK_VIEWPORT_RATIO du viewport sur les deux axes :
	// on prend la plus petite échelle (axe le plus contraint).
	const scale = FALLBACK_VIEWPORT_RATIO * Math.min(viewportWidth / widthPx, viewportHeight / heightPx);
	if (!Number.isFinite(scale) || scale <= 0) return null;

	return {
		center: lngLatToMercator(instance.getMapView().center),
		scale,
		rotation: 0,
	};
}

/**
 * Pose le fond sur la carte à partir d'un descripteur de placement (géoréférencement du plan, ou
 * repli {@link computeFallbackPlacement}) et ouvre son mode édition : l'opérateur voit
 * immédiatement les poignées de déplacement / redimensionnement / rotation.
 *
 * `layer` place le fond dans une bande de z-order donnée. Sans lui, l'image est posée dans la
 * bande du DESSUS, où l'adapter la peint au-dessus des polygones — c'est ce qu'on veut d'un fond
 * qu'on regarde seul, jamais d'un fond sous lequel on doit voir des formes (l'aperçu global des
 * formes lui passe donc une bande basse).
 *
 * `edit: false` pose l'image SANS ouvrir l'édition — c'est le cas de l'aperçu d'un calque, qu'on
 * ne cherche qu'à REGARDER : des poignées y suggéreraient un placement à valider, alors que le
 * placement réel ne se joue qu'à l'étape suivante. `shapeId` permet de la distinguer du fond.
 *
 * Retourne l'id de la forme posée.
 */
export function placeBackground(params: {
	instance: TokaMapInstance;
	importId: number;
	src: string;
	widthPx: number;
	heightPx: number;
	placement: DxfPlacement;
	shapeId?: string;
	edit?: boolean;
	layer?: number;
}): string {
	const { instance, importId, src, widthPx, heightPx, placement, edit = true, layer } = params;
	const shapeId = params.shapeId ?? backgroundShapeId(importId);

	removeBackground(instance, shapeId);

	instance.addShapeFromFeature(buildBackgroundFeature({
		shapeId,
		src,
		widthPx,
		heightPx,
		placement,
	}), undefined === layer ? SCAFFOLD_OPTIONS : { ...SCAFFOLD_OPTIONS, layer });

	// Même option `skipSave` que la création : sans elle, le premier déplacement de l'opérateur
	// déclencherait `onSave` et créerait une vraie Feature portant le data-URI du fond.
	if (edit) instance.mapAdapter.enableEditMode(shapeId, SCAFFOLD_OPTIONS);

	return shapeId;
}

/**
 * Placement MANUEL : l'opérateur trace lui-même la boîte du fond (2 clics, coin à coin, ratio du
 * plan verrouillé), comme pour n'importe quelle image importée dans l'éditeur. Utile quand le
 * pré-placement automatique tombe loin de la zone visée.
 *
 * La forme tracée est aussitôt remplacée par une forme équivalente portant l'id de fond
 * ({@link backgroundShapeId}) : `startDrawingImage` génère son propre id, et le fond doit rester
 * reconnaissable — même placement (l'aller-retour est exact), même échafaudage.
 *
 * Résout l'id du fond, ou `null` si le geste a été annulé (Échap, autre dessin démarré).
 */
export function drawBackground(params: {
	instance: TokaMapInstance;
	importId: number;
	src: string;
	widthPx: number;
	heightPx: number;
}): Promise<string | null> {
	const { instance, importId, src, widthPx, heightPx } = params;

	return new Promise<string | null>(resolve => {
		let drawn: Shape | null = null;

		// `shape:created` précède toujours `drawing:ended` (voir `finalizeImageDrawing` dans
		// `@tokamap/core`) : un `drawing:ended` sans forme créée signe une annulation. Le
		// remplacement attend cette fin de session plutôt que de retoucher le store au milieu de
		// la commande de création.
		const unsubscribe = instance.mapAdapter.on(event => {
			if (event.type === 'shape:created') {
				drawn = event.shape;
				return;
			}
			if (event.type !== 'drawing:ended') return;

			unsubscribe();
			if (!drawn) return resolve(null);

			const placement = computePlacementFromShape(drawn, widthPx);
			instance.deleteShape(drawn.id, SCAFFOLD_OPTIONS);

			resolve(placement
				? placeBackground({ instance, importId, src, widthPx, heightPx, placement })
				: null);
		});

		void instance.startDrawingImage(src, {
			width: widthPx,
			height: heightPx,
			// Déduit du `src`, comme à la pose : le drapeau choisit le mode de rendu, pas l'aspect.
			svg: src.startsWith('data:image/svg+xml'),
			...SCAFFOLD_OPTIONS,
		});
	});
}

/** Retire le fond de la carte (sans toucher à l'API : il n'y a jamais été enregistré). */
export function removeBackground(instance: TokaMapInstance, shapeId: string): void {
	if (!findBackground(instance, shapeId)) return;

	instance.mapAdapter.disableEditMode(shapeId);
	instance.deleteShape(shapeId, SCAFFOLD_OPTIONS);
}

/** La forme-image de fond, ou `null` si l'opérateur l'a supprimée entre-temps. */
export function findBackground(instance: TokaMapInstance, shapeId: string): Shape | null {
	return instance.getAllShapes().find(shape => shape.id === shapeId) ?? null;
}

/**
 * Descripteur de placement (EPSG:3857) déduit des 4 coins courants du fond — l'INVERSE exact de
 * {@link buildBackgroundFeature}, et la contrepartie de `PlacementProjector` côté serveur :
 *
 * - `center`   : centre du rectangle ;
 * - `scale`    : mètres (3857) par pixel = largeur 3857 / largeur pixel gelée du SVG ;
 * - `rotation` : angle de l'arête "largeur" du fond (radians, trigonométrique).
 *
 * L'arête largeur est celle du haut de l'image (coin 0 -> coin 1) : c'est sur elle que l'adapter
 * plaque l'image (voir `containQuad` dans `@tokamap/core`), donc c'est elle qui porte
 * l'orientation VISIBLE du plan. Elle est moyennée avec l'arête du bas (coin 3 -> coin 2) : à
 * pitch non nul, les 4 coins reprojetés forment un trapèze plutôt qu'un rectangle, et la moyenne
 * en donne la meilleure lecture rectangulaire.
 *
 * `null` si la forme n'a pas 4 coins (l'opérateur a pu lui retirer un sommet) ou si le rectangle
 * est dégénéré.
 */
export function computePlacementFromShape(shape: Shape, widthPx: number): DxfPlacement | null {
	// Anneau fermé : le dernier point répète le premier.
	const corners = shape.coordinates.slice(0, -1);
	if (corners.length !== 4 || !widthPx) return null;

	const [c0, c1, c2, c3] = corners.map(corner => lngLatToMercator(corner)) as [
		[number, number], [number, number], [number, number], [number, number],
	];

	const widthVectorX = ((c1[0] - c0[0]) + (c2[0] - c3[0])) / 2;
	const widthVectorY = ((c1[1] - c0[1]) + (c2[1] - c3[1])) / 2;
	const width = Math.hypot(widthVectorX, widthVectorY);
	if (!width) return null;

	return {
		center: [
			(c0[0] + c1[0] + c2[0] + c3[0]) / 4,
			(c0[1] + c1[1] + c2[1] + c3[1]) / 4,
		],
		scale: width / widthPx,
		rotation: Math.atan2(widthVectorY, widthVectorX),
	};
}

/**
 * Feature (format exemple.json v2, coordonnées 3857) d'une forme-image couvrant exactement la
 * boîte pixel du fond au placement donné — l'INVERSE de {@link computePlacementFromShape}, pour
 * que reposer le fond puis relire son placement retombe sur le descripteur de départ.
 *
 * Les coins suivent l'ordre attendu par le plaquage d'image (`shapeImageQuad`) : haut-gauche,
 * haut-droite, bas-droite, bas-gauche de l'IMAGE — le `y` du SVG descend là où celui du 3857
 * monte, d'où le vecteur "hauteur" pris à l'opposé du nord.
 */
function buildBackgroundFeature(params: {
	shapeId: string;
	src: string;
	widthPx: number;
	heightPx: number;
	placement: DxfPlacement;
}): LatestRawFeature {
	const { shapeId, src, widthPx, heightPx, placement } = params;

	const halfWidth = (placement.scale * widthPx) / 2;
	const halfHeight = (placement.scale * heightPx) / 2;
	const cos = Math.cos(placement.rotation);
	const sin = Math.sin(placement.rotation);
	const [centerX, centerY] = placement.center;

	// Axes de l'image dans le plan 3857 : `u` = largeur (gauche -> droite), `v` = hauteur
	// (haut -> bas de l'image, donc vers le sud quand la rotation est nulle).
	const u: [number, number] = [cos, sin];
	const v: [number, number] = [sin, -cos];

	const corner = (uSign: number, vSign: number): [number, number] => [
		centerX + uSign * halfWidth * u[0] + vSign * halfHeight * v[0],
		centerY + uSign * halfWidth * u[1] + vSign * halfHeight * v[1],
	];

	const topLeft = corner(-1, -1);

	return {
		type: 'Feature',
		geometry: {
			type: 'Polygon',
			coordinates: [[topLeft, corner(1, -1), corner(1, 1), corner(-1, 1), topLeft]],
			properties: {
				id: shapeId,
				extra: {
					type: 'image',
					// Le fond n'est qu'un repère de placement : il ne doit pas bloquer le calcul
					// d'itinéraire de la carte le temps qu'il est posé.
					walkable: true,
					image: {
						src,
						// Dimensions connues : évite à l'adapter de sonder l'image pour son ratio.
						width: widthPx,
						height: heightPx,
						// ⚠️ Déduit du `src`, jamais supposé. Le drapeau ne dit pas « c'est joli en
						// vectoriel » : il choisit le MODE DE RENDU. Un SVG est posé en surcouche
						// DOM au-dessus du canvas ; un raster devient un vrai layer MapLibre, à sa
						// place dans l'ordre des calques. Annoncer un PNG comme un SVG le faisait
						// donc peindre AU-DESSUS de tout — y compris des formes de l'aperçu global
						// qu'il est censé porter, qu'on ne voyait tout simplement pas.
						svg: src.startsWith('data:image/svg+xml'),
					},
				},
			},
		},
	};
}
